"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown, Inbox } from "lucide-react";
import {
  PRODUCT_NAV_SECTIONS,
  getSectionSub1List,
} from "@/lib/navigation/productCategories";
import { cn } from "@/lib/utils";

function AccordionButton({ isOpen, onClick, children, className }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={isOpen}
      className={cn(
        "flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-right text-sm font-medium text-white transition-colors hover:bg-white/5",
        className
      )}
    >
      <span className="flex-1">{children}</span>
      <ChevronDown
        size={16}
        aria-hidden="true"
        className={cn(
          "shrink-0 text-mute transition-transform duration-200",
          isOpen && "rotate-180"
        )}
      />
    </button>
  );
}

function NavLink({ href, onClick, children, className }) {
  if (!href) {
    return (
      <span className={cn("block px-4 py-2.5 text-sm text-mute", className)}>
        {children}
      </span>
    );
  }

  return (
    <Link
      href={href}
      onClick={onClick}
      className={cn(
        "block rounded-lg px-4 py-2.5 text-sm text-mute transition-colors hover:bg-white/5 hover:text-white",
        className
      )}
    >
      {children}
    </Link>
  );
}

export default function MobileCategoryNav({ onClose }) {
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedSub1, setExpandedSub1] = useState(null);

  const toggleSection = (sectionId) => {
    setExpandedSection((prev) => (prev === sectionId ? null : sectionId));
    setExpandedSub1(null);
  };

  const toggleSub1 = (sub1Key) => {
    setExpandedSub1((prev) => (prev === sub1Key ? null : sub1Key));
  };

  return (
    <div className="w-full">
      <div className="mb-3 flex items-center gap-3 px-1">
        <Inbox size={18} className="shrink-0 text-mute" />
        <span className="text-base font-medium text-white">دسته بندی محصولات</span>
      </div>

      <Link
        href="/categories"
        onClick={onClose}
        className="mb-2 block rounded-xl border border-white/10 px-4 py-3 text-sm font-medium text-berry-500 transition-colors hover:bg-white/5"
      >
        مشاهده همه دسته‌بندی‌ها
      </Link>

      <div className="space-y-1">
        {PRODUCT_NAV_SECTIONS.map((section) => {
          const isSectionOpen = expandedSection === section.id;
          const sub1List = getSectionSub1List(section);
          const hasSub1 = sub1List.length > 0;

          if (!hasSub1) {
            return (
              <NavLink
                key={section.id}
                href={section.href}
                onClick={onClose}
                className="px-4 py-3"
              >
                {section.title}
              </NavLink>
            );
          }

          return (
            <div
              key={section.id}
              className="rounded-xl border border-white/5 bg-white/[0.02]"
            >
              <AccordionButton
                isOpen={isSectionOpen}
                onClick={() => toggleSection(section.id)}
              >
                {section.title}
              </AccordionButton>

              {isSectionOpen && (
                <div className="border-t border-white/5 px-2 pb-2 pt-1">
                  {section.href && (
                    <NavLink
                      href={section.href}
                      onClick={onClose}
                      className="text-white-body"
                    >
                      همه {section.title}
                    </NavLink>
                  )}

                  {sub1List.map((sub1, index) => {
                    const sub1Key = sub1.key ?? `${section.id}-sub1-${index}`;
                    const hasSub2 = (sub1.sub2?.length ?? 0) > 0;
                    const isSub1Open = expandedSub1 === sub1Key;

                    if (!hasSub2) {
                      return (
                        <NavLink
                          key={sub1Key}
                          href={sub1.href}
                          onClick={onClose}
                        >
                          {sub1.title}
                        </NavLink>
                      );
                    }

                    return (
                      <div key={sub1Key}>
                        <AccordionButton
                          isOpen={isSub1Open}
                          onClick={() => toggleSub1(sub1Key)}
                          className="px-2 py-2 text-sm font-normal text-mute"
                        >
                          {sub1.title}
                        </AccordionButton>

                        {isSub1Open && (
                          <div className="mr-3 border-r border-white/10 pr-2">
                            {sub1.href && (
                              <NavLink
                                href={sub1.href}
                                onClick={onClose}
                                className="text-white-body"
                              >
                                همه {sub1.title}
                              </NavLink>
                            )}
                            {(sub1.sub2 || []).map((item, sub2Index) => {
                              const itemKey =
                                item.href ??
                                item.slug ??
                                `${sub1Key}-sub2-${sub2Index}`;
                              return (
                                <NavLink
                                  key={itemKey}
                                  href={item.href}
                                  onClick={onClose}
                                >
                                  {item.title}
                                </NavLink>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
